import { useState } from 'react';
import type { AuthUser } from '@/features/auth/stores/authStore';
import { useUpdateUser } from '../hooks/useUser';
import { useConfirm } from '@/shared/contexts/confirm-context';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel } from '@/components/ui/field';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { Pencil } from 'lucide-react';
import { normalizeApiError } from '@/shared/utils/errorHandler';
import { useDepartments } from '@/shared/hooks/useDepartments';

interface EditUserDialogProps {
  user: AuthUser;
}

export const EditUserDialog = ({ user }: EditUserDialogProps) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    userId: user.id,
    firstName: user.firstName,
    otherNames: user.otherNames,
    email: user.email,
    phoneNumber: user.phoneNumber ?? '',
    departmentId: user.departmentId ?? '',
    mfaEnabled: user.mfaEnabled ?? false,
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [globalError, setGlobalError] = useState<string | null>(null);

  const updateMutation = useUpdateUser(user.id);
  const confirm = useConfirm();
  const { data: departments = [] } = useDepartments();

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
    if (globalError) setGlobalError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setGlobalError(null);

    const payload = {
      userId: formData.userId,
      firstName: formData.firstName,
      otherNames: formData.otherNames,
      email: formData.email,
      phoneNumber: formData.phoneNumber.trim() || undefined,
      departmentId: formData.departmentId || null,
      mfaEnabled: formData.mfaEnabled,
    };

    confirm({
      title: "Save user changes?",
      description: `Changes to ${`${formData.firstName} ${formData.otherNames}`.trim()} will be saved.`,
      confirmLabel: "Save changes",
      onConfirm: () =>
        updateMutation
          .mutateAsync(payload)
          .then(() => {
            setOpen(false);
          })
          .catch((error: unknown) => {
            const normalized = normalizeApiError(error);
            if (normalized.errors && Object.keys(normalized.errors).length > 0) {
              setFieldErrors(normalized.errors);
            } else {
              setGlobalError(normalized.message);
            }
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground w-full justify-start">
          <Pencil className="w-4 h-4" />
          Edit
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>
            Update user information
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {globalError && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {globalError}
            </div>
          )}

          <Field>
            <FieldLabel htmlFor="firstName">First Name</FieldLabel>
            <Input
              id="firstName"
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              required
              disabled={updateMutation.isPending}
            />
            {fieldErrors.firstName && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.firstName.join(', ')}</p>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="otherNames">Other Names</FieldLabel>
            <Input
              id="otherNames"
              value={formData.otherNames}
              onChange={(e) => handleChange('otherNames', e.target.value)}
              required
              disabled={updateMutation.isPending}
            />
            {fieldErrors.otherNames && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.otherNames.join(', ')}</p>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="email">Email</FieldLabel>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
              disabled={updateMutation.isPending}
            />
            {fieldErrors.email && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.email.join(', ')}</p>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="phoneNumber">
              Phone Number{' '}
              <span className="text-gray-400 font-normal text-xs">(optional)</span>
            </FieldLabel>
            <Input
              id="phoneNumber"
              value={formData.phoneNumber}
              onChange={(e) => handleChange('phoneNumber', e.target.value)}
              disabled={updateMutation.isPending}
              placeholder="+254712345678"
            />
            {fieldErrors.phoneNumber && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.phoneNumber.join(', ')}</p>
            )}
          </Field>

          <Field>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.mfaEnabled}
                onChange={(e) => setFormData((prev) => ({ ...prev, mfaEnabled: e.target.checked }))}
                disabled={updateMutation.isPending || !formData.phoneNumber.trim()}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm font-medium">
                Enable two-factor authentication (SMS OTP)
              </span>
            </label>
            {!formData.phoneNumber.trim() && (
              <p className="text-xs text-muted-foreground mt-1">
                A phone number is required to enable MFA.
              </p>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="departmentId">
              Department{' '}
              <span className="text-gray-400 font-normal text-xs">(optional)</span>
            </FieldLabel>
            <Select
              value={formData.departmentId}
              onValueChange={(value) => handleChange('departmentId', value === '__none__' ? '' : value)}
              disabled={updateMutation.isPending}
            >
              <SelectTrigger id="departmentId" className="w-full">
                <SelectValue placeholder="No department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No department</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Button
            type="submit"
            className="w-full"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
